/**
 * Utility for mapping file types to appropriate icons
 */

import type { IconName } from '#ui/MaterialIcon.js';

import { getFileTypeIconName } from '@elara/client-core/lib/file-icon';

/**
 * Get the appropriate icon name and color class for a file type. The icon name comes from the
 * shared `getFileTypeIconName` (kept in sync with the native attachment previews); the color class
 * is web-only presentation, so it stays here rather than in the shared mapping.
 * @param mimeType - The MIME type of the file
 * @param fileName - The file name (used as fallback for extension matching)
 * @returns The icon name and color class
 */
export function getFileTypeIcon(mimeType?: string, fileName?: string): { iconName: IconName; className: string } {
  return { iconName: getFileTypeIconName(mimeType, fileName), className: getFileTypeColorClassName(mimeType, fileName) };
}

/** Color class companion to {@link getFileTypeIconName} — mirrors its MIME/extension branches. */
function getFileTypeColorClassName(mimeType?: string, fileName?: string): string {
  if (mimeType) {
    if (mimeType.startsWith('image/')) return 'text-blue-500';
    if (mimeType === 'application/pdf') return 'text-red-500';
    if (
      mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return 'text-blue-600';
    }
    if (
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      return 'text-green-600';
    }
    if (
      mimeType === 'application/vnd.ms-powerpoint' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
      return 'text-orange-500';
    }
    if (mimeType.startsWith('text/')) return 'text-gray-600';
    if (
      mimeType === 'application/zip' ||
      mimeType === 'application/x-zip-compressed' ||
      mimeType === 'application/x-rar-compressed' ||
      mimeType === 'application/x-7z-compressed'
    ) {
      return 'text-purple-500';
    }
    if (mimeType.startsWith('audio/')) return 'text-pink-500';
    if (mimeType.startsWith('video/')) return 'text-indigo-500';
    if (
      mimeType === 'application/javascript' ||
      mimeType === 'application/typescript' ||
      mimeType === 'application/json' ||
      mimeType === 'application/xml'
    ) {
      return 'text-yellow-600';
    }
  }

  const ext = fileName?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
    case 'bmp':
      return 'text-blue-500';
    case 'pdf':
      return 'text-red-500';
    case 'doc':
    case 'docx':
      return 'text-blue-600';
    case 'xls':
    case 'xlsx':
      return 'text-green-600';
    case 'ppt':
    case 'pptx':
      return 'text-orange-500';
    case 'txt':
    case 'md':
    case 'rtf':
      return 'text-gray-600';
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'json':
    case 'xml':
    case 'html':
    case 'css':
    case 'py':
    case 'java':
    case 'cpp':
    case 'c':
    case 'h':
      return 'text-yellow-600';
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return 'text-purple-500';
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
    case 'ogg':
      return 'text-pink-500';
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
    case 'webm':
      return 'text-indigo-500';
    default:
      return 'text-gray-500';
  }
}

/**
 * Extract a display filename from a storage URL (e.g. Firebase Storage download URL).
 * Strips query params, decodes URI components, and returns just the final path segment.
 * @param url - The full URL
 * @param options.stripExtension - If true, removes the file extension
 * @returns The extracted filename, or a fallback like "Attachment 1"
 */
export function getFilenameFromUrl(url: string, options?: { stripExtension?: boolean; fallback?: string }): string {
  const fallback = options?.fallback ?? 'Attachment';
  let name;
  try {
    name = decodeURIComponent(url.split('?')[0] ?? '').split('/').pop() ?? fallback;
  } catch {
    name = (url.split('/').pop() ?? '').split('?')[0] ?? fallback;
  }
  if (options?.stripExtension) {
    name = name.replace(/\.[^.]+$/, '');
  }
  return name;
}

/**
 * Check if a file is an image based on MIME type
 */
export function isImageFile(mimeType?: string): boolean {
  return mimeType?.startsWith('image/') || false;
}
